/**
 * AuditLog page — admin-only activity feed.
 *
 * Backend:
 *   - GET /api/audit-log?entity_type=...&action=...&actor_id=...&desde=...&hasta=...&q=...&limit=...&skip=...
 *   - GET /api/audit-log/meta → {entity_types, actions, actors, total}
 *   - DELETE /api/audit-log/purge?older_than_days=N
 *
 * UI:
 *   - Filters bar: entity, action, actor, desde, hasta, search (text)
 *   - Table with one row per entry
 *   - Click row → expand to show JSON diff/snapshot
 *   - Pagination (50 per page) + delete-old button
 */
import { useEffect, useState, useMemo, useCallback, Fragment } from "react";
import { api } from "@/App";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Activity, ChevronDown, ChevronRight, Filter, Search, X,
  Plus, Pencil, Trash2, RotateCcw, Download, Upload, ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";
import { PageLoader } from "@/components/Spinner";

// ---- Mapping helpers ---------------------------------------------------

const ENTITY_LABEL = {
  customer: "Cliente",
  product: "Producto",
  order: "Pedido",
  invoice: "Factura",
  return: "Devolución",
  payment: "Pago",
  supplier: "Proveedor",
  expense: "Gasto",
  purchase_order: "Orden de compra",
  backup: "Respaldo",
};

const ACTION_LABEL = {
  created: "Creado",
  updated: "Editado",
  deleted: "Eliminado",
  reverted: "Revertido",
  anulada: "Anulado",
  exported: "Exportado",
  restored: "Restaurado",
};

const actionStyle = (a) => {
  switch (a) {
    case "created":   return { bg: "bg-emerald-50",  text: "text-emerald-700", border: "border-emerald-200", Icon: Plus };
    case "updated":   return { bg: "bg-blue-50",     text: "text-[rgb(0,47,167)]", border: "border-blue-200", Icon: Pencil };
    case "deleted":   return { bg: "bg-rose-50",     text: "text-rose-700",    border: "border-rose-200", Icon: Trash2 };
    case "reverted":  return { bg: "bg-amber-50",    text: "text-amber-700",   border: "border-amber-200", Icon: RotateCcw };
    case "anulada":   return { bg: "bg-orange-50",   text: "text-orange-700",  border: "border-orange-200", Icon: ShieldAlert };
    case "exported":  return { bg: "bg-slate-100",   text: "text-slate-700",   border: "border-slate-200", Icon: Download };
    case "restored":  return { bg: "bg-violet-50",   text: "text-violet-700",  border: "border-violet-200", Icon: Upload };
    default:          return { bg: "bg-slate-100",   text: "text-slate-700",   border: "border-slate-200", Icon: Activity };
  }
};

function ActionBadge({ action }) {
  const s = actionStyle(action);
  const Icon = s.Icon;
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium rounded-sm border ${s.bg} ${s.text} ${s.border}`}>
      <Icon className="h-3 w-3" />
      {ACTION_LABEL[action] || action}
    </span>
  );
}

function ChangesView({ changes }) {
  /** Render a friendly diff. Detects field-level diff shape
   *  ({field: {before, after}}) vs. flat snapshot/changes object. */
  if (!changes || (typeof changes === "object" && Object.keys(changes).length === 0)) {
    return <p className="text-xs text-slate-500 italic">(sin detalles)</p>;
  }
  // Detect "diff" shape: every value is {before, after}
  const isFieldDiff = typeof changes === "object" && !Array.isArray(changes) &&
    Object.values(changes).every(v => v && typeof v === "object" && "before" in v && "after" in v);
  if (isFieldDiff) {
    return (
      <table className="w-full text-xs" data-testid="audit-diff-table">
        <thead>
          <tr className="border-b border-slate-200 text-slate-600">
            <th className="text-left py-1 pr-3 font-medium">Campo</th>
            <th className="text-left py-1 pr-3 font-medium">Antes</th>
            <th className="text-left py-1 font-medium">Después</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(changes).map(([k, v]) => (
            <tr key={k} className="border-b border-slate-100">
              <td className="py-1 pr-3 font-medium mono">{k}</td>
              <td className="py-1 pr-3 text-rose-700 mono break-all">
                {v.before === null || v.before === undefined || v.before === ""
                  ? <span className="text-slate-400 italic">—</span>
                  : JSON.stringify(v.before)}
              </td>
              <td className="py-1 text-emerald-700 mono break-all">
                {v.after === null || v.after === undefined || v.after === ""
                  ? <span className="text-slate-400 italic">—</span>
                  : JSON.stringify(v.after)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }
  // Fallback: pretty-print JSON snapshot
  return (
    <pre className="text-xs bg-slate-50 border border-slate-200 rounded-sm p-2 overflow-x-auto mono">
      {JSON.stringify(changes, null, 2)}
    </pre>
  );
}

// ---- Main page --------------------------------------------------------

const PAGE_SIZE = 50;

/**
 * @param {object} props
 * @param {object} [props.fixedFilters] - filters that the embedded variant
 *   wants to ALWAYS apply (e.g. {actor_role:"seller"} for the Sellers page,
 *   or {actor_id:"<id>"} for a specific seller). They are merged into every
 *   request and the matching dropdowns are hidden so the user can't relax
 *   the lock.
 * @param {string} [props.title]    - heading (defaults to "Registro de actividad").
 * @param {string} [props.subtitle] - description below heading.
 * @param {boolean} [props.embedded] - if true, omits the big page heading
 *   so it fits inside another page's tab.
 */
export default function AuditLog({ fixedFilters = {}, title, subtitle, embedded = false } = {}) {
  const [meta, setMeta] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [expanded, setExpanded] = useState(new Set());

  // Filters — "__all__" sentinel keeps Select required-value behavior.
  const [entityType, setEntityType] = useState("__all__");
  const [action, setAction] = useState("__all__");
  const [actorId, setActorId] = useState("__all__");
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [q, setQ] = useState("");

  const hideActorFilter = "actor_id" in fixedFilters;

  const params = useMemo(() => {
    const p = { limit: PAGE_SIZE, skip: page * PAGE_SIZE, ...fixedFilters };
    if (entityType !== "__all__") p.entity_type = entityType;
    if (action !== "__all__")     p.action = action;
    // Allow selecting a specific actor unless caller pinned actor_id directly.
    if (!hideActorFilter && actorId !== "__all__") p.actor_id = actorId;
    if (desde)                    p.desde = desde;
    if (hasta)                    p.hasta = hasta;
    if (q && q.trim())            p.q = q.trim();
    return p;
  }, [entityType, action, actorId, desde, hasta, q, page, fixedFilters, hideActorFilter]);

  const loadMeta = useCallback(async () => {
    try {
      const { data } = await api.get("/audit-log/meta");
      setMeta(data);
    } catch {/* non-blocking */}
  }, []);

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/audit-log", { params });
      setRows(data.rows || []);
      setTotal(data.total || 0);
    } catch {
      toast.error("Error al cargar la auditoría");
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => { loadMeta(); }, [loadMeta]);
  useEffect(() => { loadRows(); }, [loadRows]);

  const toggleRow = (id) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const clearFilters = () => {
    setEntityType("__all__"); setAction("__all__"); setActorId("__all__");
    setDesde(""); setHasta(""); setQ(""); setPage(0);
  };

  const handlePurge = async () => {
    const dias = window.prompt("Eliminar registros más antiguos que cuántos días?\n(escribí un número entero, por ejemplo 180)", "180");
    if (!dias) return;
    const n = parseInt(dias, 10);
    if (!Number.isFinite(n) || n < 1) return toast.error("Número inválido");
    if (!window.confirm(`Esto eliminará TODOS los registros de auditoría con más de ${n} días. ¿Continuar?`)) return;
    try {
      const { data } = await api.delete(`/audit-log/purge?older_than_days=${n}`);
      toast.success(`Eliminados ${data.deleted} registros`);
      loadRows(); loadMeta();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al purgar");
    }
  };

  if (loading && !meta) return <PageLoader label="Cargando actividad…" testid="audit-log-loader" />;

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const hasAnyFilter = entityType !== "__all__" || action !== "__all__" ||
    (!hideActorFilter && actorId !== "__all__") || desde || hasta || (q && q.trim());

  return (
    <div data-testid="audit-log-page">
      {!embedded && (
        <div className="flex items-start justify-between mb-4 flex-wrap gap-2">
          <div>
            <h2 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
              {title || "Registro de actividad"}
            </h2>
            <p className="text-slate-500 text-sm mt-1">
              {subtitle || "Toda la actividad importante de tu cuenta: quién creó, editó o eliminó qué, y cuándo."}
            </p>
          </div>
          <Button
            onClick={handlePurge}
            variant="outline"
            size="sm"
            className="rounded-sm text-xs"
            data-testid="audit-purge-button"
          >
            <Trash2 className="h-3.5 w-3.5 mr-1" />
            Limpiar antiguos
          </Button>
        </div>
      )}

      {/* Filters */}
      <Card className="bg-white border border-slate-200 rounded-sm p-3 mb-3" data-testid="audit-filters">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-2 items-end">
          <div className="md:col-span-2">
            <label className="block text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Buscar</label>
            <div className="relative">
              <Search className="h-3.5 w-3.5 absolute left-2 top-2.5 text-slate-400 pointer-events-none" />
              <Input
                value={q}
                onChange={(e) => { setQ(e.target.value); setPage(0); }}
                placeholder="cliente, factura, vendedor…"
                className="rounded-sm h-9 text-sm pl-7"
                data-testid="audit-search-input"
              />
            </div>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Entidad</label>
            <Select value={entityType} onValueChange={(v) => { setEntityType(v); setPage(0); }}>
              <SelectTrigger className="rounded-sm h-9 text-xs" data-testid="audit-entity-filter">
                <SelectValue placeholder="Todas" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Todas las entidades</SelectItem>
                {(meta?.entity_types || []).map(t => (
                  <SelectItem key={t} value={t}>{ENTITY_LABEL[t] || t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Acción</label>
            <Select value={action} onValueChange={(v) => { setAction(v); setPage(0); }}>
              <SelectTrigger className="rounded-sm h-9 text-xs" data-testid="audit-action-filter">
                <SelectValue placeholder="Todas" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Todas las acciones</SelectItem>
                {(meta?.actions || []).map(a => (
                  <SelectItem key={a} value={a}>{ACTION_LABEL[a] || a}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Usuario</label>
            <Select value={actorId} onValueChange={(v) => { setActorId(v); setPage(0); }} disabled={hideActorFilter}>
              <SelectTrigger className="rounded-sm h-9 text-xs disabled:opacity-60" data-testid="audit-actor-filter">
                <SelectValue placeholder={hideActorFilter ? "(filtro fijo)" : "Todos"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Todos los usuarios</SelectItem>
                {(meta?.actors || []).map(a => (
                  <SelectItem key={a.actor_id} value={a.actor_id}>
                    {a.actor_name || a.actor_email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-1">
            <div>
              <label className="block text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Desde</label>
              <Input type="date" value={desde} onChange={(e) => { setDesde(e.target.value); setPage(0); }}
                     className="rounded-sm h-9 text-xs" data-testid="audit-desde-input" />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Hasta</label>
              <Input type="date" value={hasta} onChange={(e) => { setHasta(e.target.value); setPage(0); }}
                     className="rounded-sm h-9 text-xs" data-testid="audit-hasta-input" />
            </div>
          </div>
        </div>
        {hasAnyFilter && (
          <div className="mt-2 flex items-center gap-2">
            <Filter className="h-3 w-3 text-slate-400" />
            <span className="text-[11px] text-slate-500">Filtros activos</span>
            <button
              onClick={clearFilters}
              className="text-[11px] text-[rgb(0,47,167)] hover:underline inline-flex items-center gap-0.5"
              data-testid="audit-clear-filters"
            >
              <X className="h-3 w-3" /> Limpiar
            </button>
          </div>
        )}
      </Card>

      {/* Table */}
      <Card className="bg-white border border-slate-200 rounded-sm p-0 overflow-hidden">
        {loading ? (
          <div className="py-10 text-center text-slate-500 text-sm">Cargando…</div>
        ) : rows.length === 0 ? (
          <div className="py-10 text-center text-slate-500 text-sm" data-testid="audit-empty">
            No hay registros que coincidan con los filtros.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="text-left py-2 px-3 font-medium text-slate-700 w-6"></th>
                  <th className="text-left py-2 px-3 font-medium text-slate-700">Fecha</th>
                  <th className="text-left py-2 px-3 font-medium text-slate-700">Acción</th>
                  <th className="text-left py-2 px-3 font-medium text-slate-700">Entidad</th>
                  <th className="text-left py-2 px-3 font-medium text-slate-700">Descripción</th>
                  <th className="text-left py-2 px-3 font-medium text-slate-700">Usuario</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const isOpen = expanded.has(r.id);
                  const Chevron = isOpen ? ChevronDown : ChevronRight;
                  const d = (r.created_at || "").slice(0, 19).replace("T", " ");
                  return (
                    <Fragment key={r.id}>
                      <tr
                        onClick={() => toggleRow(r.id)}
                        className="border-b border-slate-100 hover:bg-blue-50/40 cursor-pointer"
                        data-testid={`audit-row-${r.id}`}
                      >
                        <td className="py-2 px-3 text-slate-400">
                          <Chevron className="h-3.5 w-3.5" />
                        </td>
                        <td className="py-2 px-3 mono text-xs text-slate-600">{d}</td>
                        <td className="py-2 px-3"><ActionBadge action={r.action} /></td>
                        <td className="py-2 px-3">
                          <Badge variant="secondary" className="text-[11px] font-normal rounded-sm">
                            {ENTITY_LABEL[r.entity_type] || r.entity_type}
                          </Badge>
                        </td>
                        <td className="py-2 px-3 text-slate-700">{r.entity_label || <span className="text-slate-400">(sin etiqueta)</span>}</td>
                        <td className="py-2 px-3 text-slate-600 text-xs">
                          <div className="font-medium">{r.actor_name || "—"}</div>
                          <div className="text-slate-400">{r.actor_email}</div>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="bg-slate-50/60" data-testid={`audit-detail-${r.id}`}>
                          <td colSpan={6} className="py-3 px-6">
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-2 text-xs">
                              <div><span className="text-slate-500 uppercase tracking-wider text-[9px]">ID</span><div className="mono">{r.entity_id || "—"}</div></div>
                              <div><span className="text-slate-500 uppercase tracking-wider text-[9px]">Rol</span><div>{r.actor_role}</div></div>
                              <div><span className="text-slate-500 uppercase tracking-wider text-[9px]">IP</span><div className="mono">{r.ip || "—"}</div></div>
                              <div><span className="text-slate-500 uppercase tracking-wider text-[9px]">Navegador</span><div className="truncate" title={r.user_agent}>{r.user_agent ? r.user_agent.slice(0, 40) + "…" : "—"}</div></div>
                            </div>
                            <div>
                              <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Cambios</p>
                              <ChangesView changes={r.changes} />
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        <div className="flex items-center justify-between px-3 py-2 border-t border-slate-200 bg-slate-50">
          <div className="text-xs text-slate-500">
            Página <span className="font-bold">{page + 1}</span> de <span className="font-bold">{totalPages}</span> · {total} registro(s)
          </div>
          <div className="flex gap-1">
            <Button
              variant="outline" size="sm" className="rounded-sm h-7 text-xs"
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
              data-testid="audit-prev-page"
            >Anterior</Button>
            <Button
              variant="outline" size="sm" className="rounded-sm h-7 text-xs"
              onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              data-testid="audit-next-page"
            >Siguiente</Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
